import { ExternalLinkIcon } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { centsLabel, safeDocumentUrl, settleProblem, type SettleOutcome } from '@/lib/billing'
import { formatMicros } from '@/lib/format'

import { resolveRefund, settleRecharge, useStaffMutation, type RefundResult, type RefundRow, type StuckRecharge } from './api'
import { SELECT_CLASS } from './shared'
import { ReasonDialog } from './ui'
import { useState } from 'react'

export function RefundStateBadge({ state }: { state: string }) {
  const lower = state.toLowerCase()
  const variant =
    state === 'DONE' ? 'success' : state === 'PENDING_MANUAL' || state === 'FAILED' ? 'destructive' : 'secondary'
  return <Badge variant={variant}>{lower.replace('_', ' ')}</Badge>
}

/** A credit note's link, only when it is an https URL. */
export function CreditNoteLink({ row }: { row: Pick<RefundRow, 'credit_note_id' | 'credit_note_url'> }) {
  const url = safeDocumentUrl(row.credit_note_url)
  if (row.credit_note_id === null) return <span className="text-muted-foreground">—</span>
  if (url === null) return <span className="font-mono text-xs break-all">{row.credit_note_id}</span>
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 font-mono text-xs break-all text-primary underline-offset-2 hover:underline"
    >
      {row.credit_note_id}
      <ExternalLinkIcon className="size-3 shrink-0" />
    </a>
  )
}

/**
 * What a refund did at Stripe: one credit note per top-up it touched (newest first), the tax each
 * returned, and any `PENDING_MANUAL` row, which staff must pay out another way and then resolve.
 */
export function RefundResultPanel({ result }: { result: RefundResult }) {
  return (
    <div className="mt-2 space-y-2 text-sm" data-testid="refund-result">
      <p>
        Refunded <strong className="tabular-nums">{formatMicros(result.amount_micros)}</strong> before tax across{' '}
        {result.refunds.length} credit note{result.refunds.length === 1 ? '' : 's'}.
      </p>
      {result.pending_manual ? (
        <p className="font-medium text-destructive" data-testid="refund-pending-manual">
          At least one card refund did not go through (PENDING_MANUAL). The credit note exists: pay the
          customer another way, then mark it resolved on the account page.
        </p>
      ) : null}
      <ul className="space-y-1">
        {result.refunds.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2" data-testid="refund-result-row">
            <RefundStateBadge state={r.state} />
            <span className="tabular-nums">{formatMicros(r.amount_micros)}</span>
            <span className="text-xs text-muted-foreground tabular-nums">
              + tax {centsLabel(r.tax_cents)} = {centsLabel(r.total_cents)}
            </span>
            <CreditNoteLink row={r} />
          </li>
        ))}
      </ul>
    </div>
  )
}

/** T9: a PENDING_MANUAL refund staff paid another way. Audited, with a reason. */
export function ResolveRefundDialog({
  refund,
  onClose,
  onDone,
}: {
  refund: Pick<RefundRow, 'id' | 'total_cents' | 'failure_code'>
  onClose: () => void
  onDone: (notified: boolean | undefined) => void
}) {
  const mutation = useStaffMutation(({ reason }: { reason: string }) => resolveRefund(refund.id, reason))
  return (
    <ReasonDialog
      title="Mark this refund as paid out?"
      description={
        <>
          The card refund of <strong>{centsLabel(refund.total_cents)}</strong> did not go through
          {refund.failure_code === null ? '' : ` (${refund.failure_code})`}. Confirm only once you have paid
          the customer another way. The account’s administrators are emailed with your reason.
        </>
      }
      confirmLabel="Mark as paid out"
      testId="resolve-refund-dialog"
      onClose={onClose}
      onConfirm={async (reason) => {
        const r = await mutation.mutateAsync({ reason })
        onDone(r.notified)
      }}
    />
  )
}

/**
 * T10 / TA9: settle an auto-recharge stuck UNKNOWN. The operator has looked in Stripe: "charged"
 * credits the balance (the server fetches the object and checks status, amount and our metadata, so a
 * wrong id is refused and a repeat never credits twice); "not charged" frees the slot.
 */
export function SettleDialog({
  recharge,
  onClose,
  onDone,
}: {
  recharge: StuckRecharge
  onClose: () => void
  onDone: (outcome: SettleOutcome, notified: boolean | undefined) => void
}) {
  const [outcome, setOutcome] = useState<SettleOutcome>('charged')
  const [stripeId, setStripeId] = useState(recharge.invoice_id ?? recharge.payment_intent_id ?? '')
  const problem = settleProblem(outcome, stripeId)
  const mutation = useStaffMutation(({ reason }: { reason: string }) => settleRecharge(recharge.recharge_id, outcome, stripeId, reason))
  return (
    <ReasonDialog
      title={`Settle a ${formatMicros(recharge.amount_micros)} recharge`}
      description={
        <>
          Check Stripe first. If the customer was charged, the balance is credited with the amount before
          tax; if not, the slot is freed and auto-recharge may try again. Either way it is audited and the
          account’s administrators are emailed with your reason.
        </>
      }
      confirmLabel={outcome === 'charged' ? 'Mark as charged' : 'Mark as not charged'}
      extraValid={problem === null}
      testId="settle-dialog"
      onClose={onClose}
      onConfirm={async (reason) => {
        const r = await mutation.mutateAsync({ reason })
        onDone(outcome, r.notified)
      }}
    >
      <div className="grid gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="settle-outcome">What happened in Stripe</Label>
          <select id="settle-outcome" className={SELECT_CLASS} value={outcome} onChange={(e) => { setOutcome(e.target.value as SettleOutcome) }}>
            <option value="charged">The customer was charged</option>
            <option value="not_charged">The customer was not charged</option>
          </select>
        </div>
        {outcome === 'charged' ? (
          <div className="space-y-1.5">
            <Label htmlFor="settle-id">Stripe PaymentIntent or invoice id</Label>
            <Input id="settle-id" className="font-mono text-xs" placeholder="in_… or pi_…" value={stripeId} aria-invalid={problem !== null} onChange={(e) => { setStripeId(e.target.value) }} />
            <p className="text-xs text-muted-foreground">{problem ?? 'The server checks it against Stripe before crediting.'}</p>
          </div>
        ) : null}
      </div>
    </ReasonDialog>
  )
}
